import { supabase } from '@/integrations/supabase/client';

// Function to update user status
export const updateUserStatus = async (email: string, status: 'active' | 'inactive' | 'on-leave') => {
  console.log(`🔧 Updating user ${email} status to ${status}...`);
  
  try {
    const { data, error } = await supabase
      .from('team_members')
      .update({ status: status })
      .eq('email', email)
      .select();

    if (error) {
      console.error('❌ Error updating user status:', error);
      return false;
    }

    console.log('✅ User status updated successfully:', data);
    return true;
  } catch (error) {
    console.error('❌ Error in updateUserStatus:', error);
    return false;
  }
};

// Function to automatically fix Yogi Novinto status
export const fixYogiStatus = async () => {
  console.log('🔧 Starting to fix Yogi Novinto status...');
  
  try {
    // First, check current status
    console.log('📊 Checking current team member status...');
    const debugResult = await debugTeamStatus();
    
    if (!debugResult) {
      console.error('❌ Failed to get debug data');
      return;
    }
    
    console.log('📊 Debug result:', debugResult);
    
    // Find Yogi Novinto
    const yogi = debugResult.allMembers.find((member: any) => 
      member.name.toLowerCase().includes('yogi') || 
      member.name.toLowerCase().includes('novinto')
    );
    
    if (!yogi) {
      console.log('❌ Yogi Novinto not found in database');
      return;
    }
    
    console.log('🔍 Found Yogi Novinto:', {
      name: yogi.name,
      email: yogi.email,
      status: yogi.status,
      approval_status: (yogi as any).approval_status
    });
    
    // Check if already inactive
    if (yogi.status === 'inactive') {
      console.log('✅ Yogi Novinto is already inactive');
      return;
    }
    
    // Update status to inactive
    console.log('🔧 Updating Yogi Novinto status to inactive...');
    const updateResult = await updateUserStatus(yogi.email, 'inactive');
    
    if (updateResult) {
      console.log('✅ Successfully updated Yogi Novinto status to inactive');
      console.log('🔄 Please refresh the page to see the changes');
    } else {
      console.error('❌ Failed to update Yogi Novinto status');
    }
    
  } catch (error) {
    console.error('❌ Error in fixYogiStatus:', error);
  }
};

export const debugTeamStatus = async () => {
  console.log('🔍 Debugging team member status...');
  
  try {
    // Get all team members directly from database
    const { data, error } = await supabase
      .from('team_members')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      console.error('❌ Error fetching team members:', error);
      return;
    }

    console.log('📊 Total team members in database:', data?.length || 0);
    
    // Log all team members with their status
    data?.forEach((member: any, index: number) => {
      console.log(`👤 Member ${index + 1}:`, {
        name: member.name,
        email: member.email,
        status: member.status,
        approval_status: member.approval_status,
        role: member.role,
        created_at: member.created_at,
        updated_at: member.updated_at
      });
    });

    // Check specifically for Yogi Novinto
    const yogi = data?.find((member: any) => 
      member.name.toLowerCase().includes('yogi') || 
      member.name.toLowerCase().includes('novinto')
    );

    if (yogi) {
      console.log('🔍 Found Yogi Novinto:', {
        name: yogi.name,
        email: yogi.email,
        status: yogi.status,
        approval_status: (yogi as any).approval_status,
        role: yogi.role,
        created_at: yogi.created_at,
        updated_at: yogi.updated_at
      });
    } else {
      console.log('❌ Yogi Novinto not found in database');
    }

    // Filter active members
    const activeMembers = data?.filter((member: any) => 
      member.status === 'active' && 
      (member.approval_status === 'approved' || !member.approval_status)
    );

    console.log('✅ Active members count:', activeMembers?.length || 0);
    console.log('✅ Active members:', activeMembers?.map((m: any) => ({
      name: m.name,
      status: m.status,
      approval_status: m.approval_status
    })));

    return {
      totalMembers: data?.length || 0,
      activeMembers: activeMembers?.length || 0,
      yogiFound: !!yogi,
      yogiData: yogi || null,
      allMembers: data || []
    };

  } catch (error) {
    console.error('❌ Error in debugTeamStatus:', error);
    return null;
  }
};

// Function to force refresh team members (triggers chat component reload)
export const refreshChatMembers = async () => {
  console.log('🔄 Force refreshing chat team members...');
  
  try {
    // Trigger a custom event to refresh chat component
    const event = new CustomEvent('refreshChatMembers', {
      detail: { timestamp: Date.now() }
    });
    window.dispatchEvent(event);
    
    console.log('✅ Chat refresh event dispatched');
    
    // Also run debug to show current status
    setTimeout(async () => {
      await debugTeamStatus();
    }, 1000);
    
  } catch (error) {
    console.error('❌ Error refreshing chat members:', error);
  }
};

// Make it available globally for browser console
if (typeof window !== 'undefined') {
  (window as any).debugTeamStatus = debugTeamStatus;
  (window as any).updateUserStatus = updateUserStatus;
  (window as any).fixYogiStatus = fixYogiStatus;
  (window as any).refreshChatMembers = refreshChatMembers;
  
  // Show available debug commands
  console.log('🛠️ Debug tools loaded for team member management:');
  console.log('📊 window.debugTeamStatus() - Check all team members status');
  console.log('🔧 window.updateUserStatus(email, status) - Update user status');
  console.log('⚡ window.fixYogiStatus() - Fix Yogi Novinto avatar issue');
  console.log('🔄 window.refreshChatMembers() - Force refresh chat avatars');
  console.log('📖 See YOGI-AVATAR-FIX.md for detailed instructions');
} 